import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Button, Grid, Typography, useMediaQuery } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import SearchIcon from '@material-ui/icons/Search'
import XIcon from '@src/icons/XIcon'
import ResponsiveModal, { ModalSizes } from '@src/components/ResponsiveModal'
import LiquidFormLabel from '@src/components/LiquidFormLabel'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import { useMilliplexLazyQuery } from '@src/queries/MilliplexQuery.generated'
import { Skeleton } from '@material-ui/lab'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../../Milliplex'
import { useRouter } from 'next/router'
import AnalyteInfoMessage from '../../../components/AnalyteInfoMessage'
import { theme } from '@src/styles/theme'

const useStyles = makeStyles((theme: Theme) => {
  const grayBorder = `1px solid ${theme.palette.grey['500']}`
  return {
    buttonContainer: {
      display: 'flex',
      gap: theme.spacing(4),
      justifyContent: 'flex-end',
      margin: theme.spacing(4, 6, 4, 0),
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(4, 6, 4, 0),
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: theme.typography.pxToRem(48),
        marginTop: theme.spacing(8),
        marginBottom: theme.spacing(8),
      },
    },
    infoMessage: {
      margin: theme.spacing(4, 0),
    },
    modalBodyContainerStyle: {
      margin: theme.spacing(6, 8, 3, 8),
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2, 8, 2, 8),
      },
    },
    modalBodyTitleStyle: {
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(3, 2, 2, 4),
      },
    },
    search: {
      position: 'relative',
    },
    searchInput: {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.common.white,
      border: 'none',
      boxShadow: `inset 0 0 0 1px ${theme.palette.grey['500']}`,
      transition: 'all .3s',
      height: 32,
      boxSizing: 'border-box',
      fontSize: theme.typography.pxToRem(16),
      lineHeight: '32px',
      padding: '0 30px 0 12px',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        height: 40,
        lineHeight: '40px',
      },

      '&:hover': {
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
      },
    },
    searchIcon: {
      fontSize: theme.typography.pxToRem(20),
    },
    clearIcon: {
      fontSize: theme.typography.pxToRem(14),
    },
    searchButton: {
      position: 'absolute',
      right: 0,
      top: 1,
      width: 30,
      height: 38,
      padding: 0,
      paddingTop: 3,
      border: 0,
      background: 'none',
      color: theme.palette.primary.main,
      cursor: 'pointer',
    },
    analytesContainer: {
      marginTop: theme.spacing(4),
      maxHeight: theme.spacing(85),
      overflowY: 'auto',
    },
    availableAnalytesLable: {
      fontWeight: theme.typography.fontWeightBold,
    },
    checkboxRoot: {
      paddingBottom: theme.spacing(1),
      alignSelf: 'flex-start',
    },
    analytesList: {
      '& > :nth-child(1):not($optionBoxSelected), & > :nth-child(2):not($optionBoxSelected), & > :nth-child(3):not($optionBoxSelected)':
        {
          borderTop: grayBorder,
        },
      '& > :nth-child(3n-2):not($optionBoxSelected)': {
        borderLeft: grayBorder,
      },
      '& > :nth-child(3n-1):not($optionBoxSelected), & > :nth-child(3n):not($optionBoxSelected), & > :nth-child(3n-2):not($optionBoxSelected)':
        {
          borderRight: grayBorder,
          borderBottom: grayBorder,
        },
    },
    analytesSingle: {
      padding: theme.spacing(3),
    },
    optionBoxSelected: {
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.primary.dark}`,
    },
  }
})

interface SelectAnalytesModalProps {
  handleClose: () => void
  open: boolean
  displayPageError: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
}

const SelectAnalytesModal: React.FC<SelectAnalytesModalProps> = ({
  handleClose,
  open,
  displayPageError,
  setDisplayPageError,
}) => {
  const classes = useStyles()
  const formik = useFormikContext<MilliplexFormValues>()
  const router = useRouter()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [
    milliplexGetAnalytes,
    { data: milliplexGetAnalytesData, loading: milliplexGetAnalytesLoading },
  ] = useMilliplexLazyQuery()

  const [term, setTerm] = useState<string>('')
  const [analytesArray, setAnalytesArray] = useState<string[]>([])
  const [selectedAnalytes, setSelectedAnalytes] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      milliplexGetAnalytes({
        variables: {
          input: {
            species: formik.values.species,
            researchAreas: formik.values.researchAreas,
            analytes: [], // TODO: API has dependency with analytes and need empty for few species. Remove once API has updated.
            plateType: formik.values.plateType,
            instrumentTypes: formik.values.instrumentType || [],
            selectedAssayType: formik.values.assay,
          },
        },
      })
      setSelectedAnalytes(formik.values.analytes)
    }
  }, [open])

  useEffect(() => {
    const listAnalytes =
      (milliplexGetAnalytesData?.getMilliplex?.listAnalytes as string[]) || []
    setAnalytesArray(listAnalytes)
  }, [milliplexGetAnalytesData])

  const clearSearch = () => {
    term && setTerm('')
  }
  const onClose = () => {
    clearSearch()
    handleClose()
  }

  const onDoneButtonClick = () => {
    //disable page error once an analyte got selected
    if (selectedAnalytes.length > 0 && displayPageError)
      setDisplayPageError(false)
    formik.setFieldValue('analytes', selectedAnalytes)
    formik.setFieldTouched('analytes', false)
    router.replace(
      {
        query: {
          ...router.query,
          analytes: selectedAnalytes,
        },
      },
      undefined,
      { scroll: false }
    )
    clearSearch()
    handleClose()
  }
  const handleChange = (event) => {
    setTerm(event.target.value)
  }
  const handleAnalytesChange = (e) => {
    const checked = e.target.checked,
      value = e.target.value
    const filteredValues = selectedAnalytes.filter(
      (analyte) => analyte !== value
    )
    checked && filteredValues.push(value)
    setSelectedAnalytes(filteredValues)
  }

  const escapeRegExp = (str) => str.replace(/[-[\]{}()*+?.\\^$|]/g, '\\$&')
  const filterByTerm = (data: string) =>
    data.match(new RegExp(escapeRegExp(term.trim()), 'i'))

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      renderTitle={() => (
        <Typography
          variant="h2"
          component="h2"
          className={classes.modalBodyTitleStyle}
        >
          <FormattedMessage
            id="PC_ANALYTES_milliplex"
            defaultMessage="Select Analytes"
          />
        </Typography>
      )}
      size={ModalSizes.Large}
      disableEscapeKeyDown
    >
      <Box className={classes.modalBodyContainerStyle}>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <LiquidFormLabel>
              <FormattedMessage
                id="MILLIPLEX_SEARCH_ANALYTES"
                defaultMessage="Search Analytes"
              />
            </LiquidFormLabel>
            <Grid item xs={12} className={classes.search}>
              <input
                className={classes.searchInput}
                onChange={handleChange}
                value={term}
              />
              <button className={classes.searchButton} onClick={clearSearch}>
                {term ? (
                  <XIcon className={classes.clearIcon} />
                ) : (
                  <SearchIcon className={classes.searchIcon} />
                )}
              </button>
            </Grid>
          </Grid>
          {milliplexGetAnalytesLoading ? (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {Array(15)
                  .fill(undefined)
                  .map((_, index) => (
                    <Grid item xs={4} key={index}>
                      <Skeleton animation="wave" height={50} />
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          ) : (
            <Grid item xs={12} className={classes.analytesContainer}>
              <LiquidFormLabel className={classes.availableAnalytesLable}>
                <FormattedMessage
                  id="MILLIPLEX_AVAILABLE_ANALYTES"
                  defaultMessage="Available Analytes"
                />
              </LiquidFormLabel>
              <Grid container className={classes.analytesList}>
                {analytesArray.filter(filterByTerm).length > 0 ? (
                  analytesArray
                    .filter(filterByTerm)
                    .sort((a, b) =>
                      a.toLowerCase().localeCompare(b.toLowerCase())
                    )
                    .map((option) => {
                      const checked = selectedAnalytes.indexOf(option) >= 0
                      return (
                        <Grid
                          item
                          xs={4}
                          key={option}
                          className={clsx(classes.analytesSingle, {
                            [classes.optionBoxSelected]: checked,
                          })}
                        >
                          <LiquidCheckbox
                            aria-label={option}
                            value={option}
                            checked={checked}
                            classes={{
                              root: classes.checkboxRoot,
                            }}
                            onChange={handleAnalytesChange}
                            label={option}
                          />
                        </Grid>
                      )
                    })
                ) : (
                  <FormattedMessage
                    id="MILLIPLEX_NO_ANALYTES_FOUND"
                    defaultMessage="No Analytes Found!"
                  />
                )}
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid container>
          <AnalyteInfoMessage analytes={analytesArray} />
          <Grid item xs={12} className={classes.buttonContainer}>
            {!isMobile && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={onClose}
              >
                <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
              </Button>
            )}
            <Button
              color="primary"
              variant="contained"
              size="large"
              onClick={onDoneButtonClick}
              disabled={selectedAnalytes?.length === 0}
            >
              <FormattedMessage
                id="MILLIPLEX_ADD_ANALYTES"
                defaultMessage="Add Analytes"
              />
            </Button>
          </Grid>
        </Grid>
      </Box>
    </ResponsiveModal>
  )
}
export default SelectAnalytesModal
